import React, { FunctionComponent, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import { LoginTypeEnum, AdminOrganizationDetails } from 'api';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import Icon from 'src/ui/atoms/icon';
import Section from 'src/ui/atoms/section';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import useIsMedia, { DEVICES } from 'src/ui/hooks/use-is-media';
import { handleError } from 'src/ui/helper/error-handling';
import useTranslate, { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import ServerError from 'src/ui/types/server-error';
import Header from 'src/ui/atoms/header';
import * as Styled from './styled';
import Button from 'src/ui/atoms/button';
import FormGroup from 'src/ui/molecules/form-group';
import Select from 'src/ui/atoms/select';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import Hint from 'ui/atoms/hint';

interface AddOrganizationModalProps {
  onClose: () => void;
  onSubmit: (values: StateValues<AddOrganizationFields>) => void;
  data?: AdminOrganizationDetails;
  error?: ServerError | null;
  cumulativeEntryRequiredButMissing?: boolean;
  singleEntryRequiredButMissing?: boolean;
}

export interface AddOrganizationFields {
  name: string;
  loginType: LoginTypeEnum;
  serviceContracts: Array<string>;
}

export const Form = makeModalForm<AddOrganizationFields>();

const MAX_CONTRACTS = 4;

const AddOrganizationModal: FunctionComponent<AddOrganizationModalProps> = ({
  data,
  error,
  onSubmit,
  onClose,
  cumulativeEntryRequiredButMissing,
  singleEntryRequiredButMissing,
}) => {
  const [serviceContracts, setServiceContracts] = useState<Array<string>>(
    data && data.serviceContracts.length > 0 ? data.serviceContracts : [''],
  );

  const isPhone = useIsMedia(DEVICES.phone);
  const translate = useTranslateWithStringArgs();
  const isEditView = !!data;

  const allServiceContractOptions = useMemo(() => {
    return ['core', 'distribution_hub', 'banking', 'launchpad'].map((value) => ({
      value,
      label: <Translate name={`studioOrganizationsTable.serviceContracts.${snakeToCamelCaseWithNumbers(value)}`} />,
    }));
  }, []);

  const availableOptions = (serviceContract: string) => {
    return allServiceContractOptions.filter((option) => {
      const isAlreadySelected = serviceContracts.includes(option.value);
      const isCurrentValue = option.value === serviceContract;

      return !isAlreadySelected || isCurrentValue;
    });
  };

  const handleAddContract = () => {
    if (serviceContracts.length < MAX_CONTRACTS) {
      setServiceContracts([...serviceContracts, '']);
    }
  };

  const handleRemoveContract = (index: number) => {
    if (serviceContracts.length > 1) {
      const updated = serviceContracts.filter((_, i) => i !== index);
      setServiceContracts(updated);
    } else {
      setServiceContracts(['']);
    }
  };

  const handleUpdateContract = (index: number, value: string) => {
    const updated = [...serviceContracts];
    updated[index] = value;
    setServiceContracts(updated);
  };

  const { getErrorForField: getNameError } = handleError({
    error,
    translate: useTranslate(),
    specialCodes: {
      unique: {
        field: 'name',
        translateKey: 'addOrganization.fields.name.uniqueError',
      },
    },
  });

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="addOrganization"
        onSubmit={(fieldValues: StateValues<AddOrganizationFields>) => {
          const values = {
            ...fieldValues,
            serviceContracts: serviceContracts.filter((item) => item !== ''),
          };

          onSubmit(values);
        }}
        error={error}
        initial={
          data
            ? {
                name: data.name,
                loginType: data.loginType,
              }
            : {}
        }
      >
        <ModalHeader>
          <Translate
            name={isEditView ? 'issuerOrganizationsDetails.editGeneralInformation' : 'addOrganization.title'}
          />
        </ModalHeader>
        <ModalContent>
          <Section spacing="regular">
            <Form.Group
              name="name"
              required={true}
              fieldError={getNameError('name')}
              tooltip={
                <Tooltip
                  content={<Translate name="addOrganization.fields.name.tooltip" />}
                  position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                >
                  <Icon name="question" size="large" color="info" padded="none" />
                </Tooltip>
              }
            >
              <Form.Input />
            </Form.Group>

            <Form.Group
              name="loginType"
              required={true}
              tooltip={
                <Tooltip
                  content={<Translate name="addOrganization.fields.loginType.tooltip" />}
                  position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                >
                  <Icon name="question" size="large" color="info" padded="none" />
                </Tooltip>
              }
            >
              <Form.Select
                options={Object.values(LoginTypeEnum).map((loginType) => ({
                  value: loginType,
                  label:
                    loginType === LoginTypeEnum.GOOGLE_SSO ? (
                      <Translate name="studioOrganizationsTable.googleSsoLogin" />
                    ) : (
                      <Translate name="studioOrganizationsTable.passwordLogin" />
                    ),
                }))}
              ></Form.Select>
            </Form.Group>
          </Section>
          <Section spacing="small">
            <Header spacing="medium">
              <Translate name="addOrganization.serviceContracts" />
            </Header>

            {serviceContracts.map((contract, index) => {
              return (
                <FormGroup
                  key={`service-contract-${index}`}
                  tooltip={
                    index === 0 ? (
                      <Tooltip
                        content={<Translate name="addOrganization.fields.serviceContracts.tooltip" />}
                        position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                      >
                        <Icon name="question" size="large" color="info" padded="none" />
                      </Tooltip>
                    ) : null
                  }
                  occupyTooltipSpace={index !== 0}
                >
                  <Styled.SelectContainer>
                    <Select
                      options={availableOptions(contract)}
                      value={contract}
                      onChange={(val) => handleUpdateContract(index, val)}
                      placeholder={translate('addOrganization.fields.serviceContracts.label')}
                    />
                    <Styled.DeleteIcon
                      name="trash"
                      color="primary"
                      disabled={serviceContracts.length === 1 && !contract}
                      onClick={() => handleRemoveContract(index)}
                    />
                  </Styled.SelectContainer>
                </FormGroup>
              );
            })}
          </Section>
          {serviceContracts.length < MAX_CONTRACTS && (
            <Button variant="link" icon="plus" size="small" onClick={handleAddContract}>
              <Translate name="addOrganization.addServiceContract" />
            </Button>
          )}
          {cumulativeEntryRequiredButMissing && (
            <Hint variant="danger">
              <Translate name="addOrganization.fields.serviceContracts.cumulativeEntryError" />
            </Hint>
          )}
          {singleEntryRequiredButMissing && (
            <Hint variant="danger">
              <Translate name="addOrganization.fields.serviceContracts.singleEntryError" />
            </Hint>
          )}
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'send',
                content: <Translate name={isEditView ? 'common.saveChanges' : 'common.add'} />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddOrganizationModal;
